import { CircularProgress, TextField, TextFieldProps } from '@mui/material';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

import { useDebounce } from '@hooks/useDebounce';

export type DebounceTextFieldHandle = {
  resetDebouncedTextFieldValue: () => void;
  setValue: (data: string) => void;
};

type Props = TextFieldProps & {
  defaultValue?: string | null;
  onChangeDebounced: (data: string) => void;
  isLoading?: boolean;
};

const DebounceTextField = forwardRef<DebounceTextFieldHandle, Props>(
  ({ onChangeDebounced, defaultValue, isLoading, ...textFieldProps }: Props, ref) => {
    const [value, setValue] = useState<string>(defaultValue || '');
    const debounceValue = useDebounce(value);

    const debounceRef = useRef(debounceValue);

    useImperativeHandle(
      ref,
      () => {
        return {
          resetDebouncedTextFieldValue: () => setValue(''),
          setValue,
        };
      },
      [],
    );

    if (debounceValue !== debounceRef.current) {
      onChangeDebounced(debounceValue);
      debounceRef.current = debounceValue;
    }

    return (
      <TextField
        fullWidth
        value={value}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setValue(event.target.value)}
        slotProps={{
          input: {
            ...textFieldProps?.InputProps,
            endAdornment: (
              <>
                {(debounceValue !== value || isLoading) ? <CircularProgress aria-label="Loading..." color="inherit" size={20} /> : null}
                {textFieldProps?.InputProps?.endAdornment}
              </>
            ),
          },
        }}
        {...textFieldProps}
      />
    );
  },
);

export default DebounceTextField;
